import {useRouter} from 'next/router';
import {type FC, useMemo} from 'react';
import {useGetColumnCategories} from '../../../../../../../core/repositories/columnRepository';
import {cn} from '../../../../../../../lib/classname';
import {
  COLUMNS_BY_ALL_CATEGORIES,
  COLUMNS_BY_ROOT_CATEGORY,
  COLUMNS_BY_SUB_CATEGORY,
} from '../../../routes';
import {CategoriesPC} from './pc';
import {CategoriesSP} from './sp';

type Props = {
  className?: string;
};

export type CategoryItem = {
  name: string;
  slug: string;
  href: string;
};

export type ActiveCategory = {
  item: CategoryItem;
  index: number;
};

export const Categories: FC<Props> = ({className}) => {
  const {data: _categories} = useGetColumnCategories({});
  const categories = useMemo(() => {
    if (!_categories?.ok) return [];
    return _categories.value.categories.filter(
      ({category}) => category.slug !== 'sponsored', // due to issue#8845
    );
  }, [_categories]);

  const {asPath} = useRouter();
  // /columns/category/[rootSlug]
  // /columns/category/[rootSlug]/[subSlug]
  const currentRootSlug = asPath.split('/')[3];
  const currentSubSlug = asPath.split('/')[4];

  const rootCategories = useMemo<CategoryItem[]>(
    () =>
      categories.map(({category}) => ({
        name: category.name,
        slug: category.slug,
        href:
          category.slug === currentRootSlug
            ? COLUMNS_BY_ALL_CATEGORIES
            : COLUMNS_BY_ROOT_CATEGORY(category.slug),
      })),
    [categories, currentRootSlug],
  );

  const activeRootCategory = useMemo<ActiveCategory | undefined>(() => {
    if (!currentRootSlug) return undefined;
    const index = rootCategories.findIndex(
      (category) => category.slug === currentRootSlug,
    );
    const item = rootCategories[index];
    if (index === -1 || !item) return undefined;
    return {
      item,
      index,
    };
  }, [currentRootSlug, rootCategories]);

  const subCategories = useMemo<CategoryItem[]>(() => {
    if (!activeRootCategory) return [];
    const activeRawRootCategory = categories.find(
      (category) => category.category.slug === activeRootCategory.item.slug,
    );
    if (!activeRawRootCategory) return [];
    const subCategories = activeRawRootCategory.children.map(({category}) => ({
      name: category.name,
      slug: category.slug,
      href: COLUMNS_BY_SUB_CATEGORY(
        activeRawRootCategory.category.slug,
        category.slug,
      ),
    }));
    // サブカテゴリが1つ以下の場合は表示しない
    if (subCategories.length <= 1) return [];
    return subCategories;
  }, [categories, activeRootCategory]);

  const activeSubCategory = useMemo<ActiveCategory | undefined>(() => {
    if (!currentSubSlug) return undefined;
    const index = subCategories.findIndex(
      (category) => category.slug === currentSubSlug,
    );
    const item = subCategories[index];
    if (index === -1 || !item) return undefined;
    return {
      item,
      index,
    };
  }, [currentSubSlug, subCategories]);

  return (
    <div className={cn(className)}>
      <div className="hidden pc:block">
        <CategoriesPC
          rootCategories={rootCategories}
          activeRootCategory={activeRootCategory}
          subCategories={subCategories}
          activeSubCategory={activeSubCategory}
        />
      </div>
      <div className="block pc:hidden">
        <CategoriesSP
          categories={categories}
          activeRootCategory={activeRootCategory}
          activeSubCategory={activeSubCategory}
        />
      </div>
    </div>
  );
};
